/**
 * Image override helpers.
 *
 * The public `/settings/web-content-overrides` endpoint returns a nested JSON
 * blob (legacy site_settings merged with admin-uploaded rows). Uploaded images
 * win over JSON for the same key.
 *
 * These helpers read the relevant slice of that blob safely — every helper
 * accepts an `overrides` object that may be `null` / empty and always returns a
 * string (the override URL when present, the supplied fallback otherwise).
 *
 *   ┌─────────────────────────────┬──────────────────────────────────────────┐
 *   │ Surface                     │ Override key path                        │
 *   ├─────────────────────────────┼──────────────────────────────────────────┤
 *   │ Treatment hero / OG image   │ overrides.treatments[slug].image         │
 *   │ Homepage popular treatments │ overrides.treatments[slug].image  OR     │
 *   │                             │ overrides.popularTreatments[slug].image/ │
 *   │                             │ imageUrl                                 │
 *   │ Homepage hero (right col)   │ overrides.home.heroImageUrl OR           │
 *   │                             │ overrides.home.heroImage                 │
 *   │ Category cards              │ overrides.categoryCards[slug].image OR   │
 *   │                             │ overrides.categoryCards[slug].imageUrl   │
 *   │ Treatment guides            │ overrides.treatmentGuides[testId].image  │
 *   │                             │   OR .imageUrl                           │
 *   │ Travel Hub destinations     │ overrides.travelHub.destinations[City]   │
 *   │                             │   .imageUrl  (city string matches data)  │
 *   └─────────────────────────────┴──────────────────────────────────────────┘
 */

const pickUrl = (obj, ...keys) => {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return null;
};

export const getTreatmentImage = (overrides, slug, fallback) => {
  if (!overrides || !slug) return fallback;
  return (
    pickUrl(overrides.treatments?.[slug], 'image', 'imageUrl')
    || fallback
  );
};

export const getPopularTreatmentImage = (overrides, slug, fallback) => {
  if (!overrides || !slug) return fallback;
  return (
    pickUrl(overrides.popularTreatments?.[slug], 'image', 'imageUrl')
    || pickUrl(overrides.treatments?.[slug], 'image', 'imageUrl')
    || fallback
  );
};

export const getHomeHeroImage = (overrides, fallback) => {
  if (!overrides) return fallback;
  return pickUrl(overrides.home, 'heroImageUrl', 'heroImage') || fallback;
};

export const getCategoryCardImage = (overrides, slug, fallback) => {
  if (!overrides || !slug) return fallback;
  return (
    pickUrl(overrides.categoryCards?.[slug], 'image', 'imageUrl')
    || fallback
  );
};

export const getTreatmentGuideImage = (overrides, testId, fallback) => {
  if (!overrides || !testId) return fallback;
  return (
    pickUrl(overrides.treatmentGuides?.[testId], 'image', 'imageUrl')
    || fallback
  );
};

export const getTravelDestinationImage = (overrides, cityName, fallback) => {
  if (!overrides || !cityName) return fallback;
  return (
    pickUrl(overrides.travelHub?.destinations?.[cityName], 'imageUrl', 'image')
    || fallback
  );
};

/**
 * Info landing hero image (per-page, admin-managed via "Info pages" tab).
 *
 *   refKey examples:
 *     'patient-support', 'doctor-recommendation', 'medical-translator',
 *     'how-to-choose-doctor', 'medical-tourism-support', 'doctor-to-hotel',
 *     'english-speaking-doctor', 'urgent-medical-care'
 */
export const getInfoLandingImage = (overrides, refKey, fallback) => {
  if (!overrides || !refKey) return fallback;
  return (
    pickUrl(overrides.infoLandings?.[refKey], 'imageUrl', 'image')
    || fallback
  );
};
